'use client'

import { useState, useMemo } from 'react'
import { IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ResponsiveModal } from '@/components/ui/responsive-modal'
import { Building2, Check, Edit, PlusIcon, Search } from 'lucide-react'
import type { IWorkspaceWithSubscription } from '@/hooks/subscriptions/use-workspaces'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import { cn } from '@/lib/utils'
import { pushModal } from '@/components/modals'
import { ProjectsListVertical } from './projects-list-vertical'
import { useTranslations } from 'next-intl'

interface WorkspaceSelectorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaces: IWorkspaceWithSubscription[]
  selectedWorkspaceId: string | null
  onWorkspaceChange: (workspaceId: string) => void
}

/**
 * Workspace selector modal (desktop) / drawer (mobile)
 * Shows all workspaces with projects, plan badges, and rename functionality
 */
export function WorkspaceSelectorModal({
  open,
  onOpenChange,
  workspaces,
  selectedWorkspaceId,
  onWorkspaceChange,
}: WorkspaceSelectorModalProps) {
  const t = useTranslations('pricing-v3')
  const tWorkspace = useTranslations('workspace-selector')
  const [searchTerm, setSearchTerm] = useState('')
  const { mutate: mutateWorkspaces } = useWorkspaces()

  /**
   * Filter workspaces based on project name search and sort by latest updated
   */
  const filteredWorkspaces = useMemo(() => {
    let result = workspaces

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = workspaces
        .map((workspace) => {
          const matchingProjects = workspace.projects.filter((project) => {
            return project.project_name.toLowerCase().includes(search)
          })

          return {
            ...workspace,
            projects: matchingProjects,
          }
        })
        .filter((workspace) => {
          return workspace.projects.length > 0
        })
    }

    // Sort by update_time (most recent first)
    return [...result].sort((a, b) => {
      const timeA = new Date(a.update_time).getTime()
      const timeB = new Date(b.update_time).getTime()
      return timeB - timeA
    })
  }, [workspaces, searchTerm])

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'platinum':
        return 'from-amber-200 via-yellow-300 to-amber-200 text-gray-900'
      case 'premium':
        return 'from-purple-500 to-blue-500 text-white'
      case 'basic':
        return 'bg-blue-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  /**
   * Handle workspace selection
   */
  const handleSelectWorkspace = (workspaceId: string) => {
    onWorkspaceChange(workspaceId)
    onOpenChange(false)
  }

  /**
   * Handle workspace rename
   */
  const handleRenameWorkspace = (
    e: React.MouseEvent,
    workspace: IWorkspaceWithSubscription,
  ) => {
    e.stopPropagation()

    pushModal('RenameWorkspaceModal', {
      workspaceId: workspace._id.toString(),
      currentName: workspace.name,
      onSuccess: () => {
        mutateWorkspaces()
      },
    })
  }

  /**
   * Render workspace card
   */
  const renderWorkspaceCard = (workspace: IWorkspaceWithSubscription) => {
    const workspaceId = workspace._id.toString()
    const isSelected = workspaceId === selectedWorkspaceId

    return (
      <button
        key={workspaceId}
        type="button"
        onClick={() => {
          return handleSelectWorkspace(workspaceId)
        }}
        className={cn(
          'w-full text-left p-4 rounded-lg border-2 transition-all',
          'hover:border-white/30',
          isSelected
            ? 'border-white/40 bg-white/10'
            : 'border-white/10 bg-white/5',
        )}
      >
        {/* Header: Workspace name, plan, actions */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="size-4 text-white/60 flex-shrink-0" />
              <h3 className="text-base font-semibold text-white truncate">
                {workspace.name || workspaceId}
              </h3>
              <div
                className={cn(
                  'inline-block text-xs px-2 py-0.5 rounded font-semibold',
                  workspace.limits.plan !== 'free'
                    ? `bg-gradient-to-r ${getPlanBadgeColor(workspace.limits.plan)}`
                    : getPlanBadgeColor(workspace.limits.plan),
                )}
              >
                {workspace.limits.plan.charAt(0).toUpperCase() +
                  workspace.limits.plan.slice(1)}
              </div>
              {isSelected && (
                <Check className="size-4 text-green-400 flex-shrink-0" />
              )}
            </div>
          </div>

          {/* Rename button - only show for owner/admin */}
          {(workspace.is_owner ||
            workspace.user_role === 'admin' ||
            workspace.user_role === 'owner') && (
            <IconButton
              size="xs"
              variant="ghost"
              onClick={(e) => {
                return handleRenameWorkspace(e, workspace)
              }}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <Edit className="size-3" />
            </IconButton>
          )}
        </div>

        {/* Projects list */}
        {workspace.projects.length > 0 && (
          <div className="p-2 border-t border-white/10">
            <ProjectsListVertical projects={workspace.projects} />
          </div>
        )}
      </button>
    )
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('select_workspace')}
      className="max-w-5xl max-h-[85vh] overflow-y-hidden"
      isModal={true}
    >
      {/* Custom header with Add Workspace button */}
      <div className="flex items-center justify-between mb-4 -mt-2">
        <div className="flex-1" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleSelectWorkspace('new')
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(
            'px-3 py-1.5 text-xs font-bold rounded-lg transition-all',
            'bg-gold-gradient text-black hover:opacity-90',
            'flex items-center gap-1.5',
            'shadow-[0_0_20px_-5px_rgba(255,215,0,0.4)] hover:shadow-[0_0_30px_-5px_rgba(255,215,0,0.6)]',
            'hover:scale-[1.02] active:scale-[0.98]',
            'uppercase tracking-wider',
          )}
        >
          <PlusIcon className="size-3.5" />
          <span>{tWorkspace('actions.add_workspace')}</span>
        </button>
      </div>
      <div className="space-y-4">
        {/* Filter input - moved to top for emphasis */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/40" />
          <Input
            type="text"
            placeholder={t('filter_workspace_by_project_name')}
            value={searchTerm}
            onChange={(e) => {
              return setSearchTerm(e.target.value)
            }}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40"
          />
        </div>

        {/* Existing workspaces - main focus */}
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {filteredWorkspaces.length > 0 ? (
            filteredWorkspaces.map((workspace) => {
              return renderWorkspaceCard(workspace)
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-white/60">
                {t('no_projects_match_search')}
              </p>
            </div>
          )}
        </div>
      </div>
    </ResponsiveModal>
  )
}
